/* eslint-disable max-statements */
import "./insGridPage.scss";
import { AuxGridColumnType, AuxTabBar } from "@blk/aladdin-react-components-es";
import { isEqual } from "lodash";
import React from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { getMeasuresAndDimensionsWithCurrency, useCreateFundsDatapointsExcelRequest } from "../../hooks/use-funds-page";
import { InsFundsHeaderLeftBenchmark } from "../frame/insFundsHeaderLeftBenchmark";
import { useFetchTagsQuery } from "../tags/tagsApi";
import { DataExportGridTypes } from "./administration/export-data-administration/insExportDataAdministration";
import { useExportExcel, useSetBenchmarkMenuItem, useSetPMIMenuItem } from "./use-menu-items";
import { DatapointIdStandardEnum, DatapointsEntityType, EntityType } from "api/ux/src";
import { InsBulkPMISelectionMenu } from "components/frame/insBulkPMISelectionMenu";
import { addCustomFieldsAsCols, addCustomFieldsAsDatapoints } from "components/modal/portfolio-creation-edit/helper";
import { prepareTagRows } from "components/tags/helpers";
import { TagEntityType } from "components/tags/types";
import { InsGridWidget } from "components/widgets";
import { getColumnExcelOptions, getDatapointTotalGroupExcelOptions } from "components/widgets/grid/excelExportOptions";
import { InsGridAggs } from "components/widgets/grid/grid-agg-funcs";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import { getDefaultMappings, insDatapointGridWidgetAdapterData } from "components/widgets/grid/insDatapointGridWidgetAdapter";
import { useCustomDataDatapointsRequest, useGetCustomDataDatapoints } from "hooks/use-custom-data-datapoints";
import { useGetCustomDatapointFields } from "hooks/widget-data/use-datapoints";
import { useRefreshOnUserSettingsChange } from "hooks/widget-data/user-settings/user-settings";
import { language } from "language/default";
import { setFundTab } from "store/grid-settings";
import { gridCurrencySelector, resetFundAndInvestorSelectedRows, setSelectedRowsState, useGetGridGroups } from "store/grids";
const { assetClass } = language.en;
export const FundsTabs = {
    Funds: "Funds",
    InvestorAccounts: "InvestorAccounts"
};
const useMeasuresAndDimensions = (currency, groups, withInvestors) => {
    const customFields = useGetCustomDatapointFields(withInvestors ? [DatapointsEntityType.Fund, "Investor Account"] : [DatapointsEntityType.Fund]);
    const datapoints = [
        { datapoint: { standard: DatapointIdStandardEnum.FundFundId }, name: "Id" },
        { datapoint: { standard: DatapointIdStandardEnum.FundManagerId }, name: "ManagerId" },
        { datapoint: { standard: DatapointIdStandardEnum.FundFundName }, name: "Name" },
        { datapoint: { standard: DatapointIdStandardEnum.FundCurrency }, name: "Currency" },
        { datapoint: { standard: DatapointIdStandardEnum.FundManagerName }, name: "ManagerName" },
        { datapoint: { standard: DatapointIdStandardEnum.FundStrategy }, name: "Strategy" },
        { datapoint: { standard: DatapointIdStandardEnum.FundGeography }, name: "Geography" },
        { datapoint: { standard: DatapointIdStandardEnum.FundNAV }, name: "NAV" },
        { datapoint: { standard: DatapointIdStandardEnum.FundCommitment }, name: "Commitment" },
        { datapoint: { standard: DatapointIdStandardEnum.FundContribution }, name: "Contributions" },
        { datapoint: { standard: DatapointIdStandardEnum.FundDistribution }, name: "Distributions" },
        { datapoint: { standard: DatapointIdStandardEnum.FundAssetClass }, name: "Asset Class" },
        { datapoint: { standard: DatapointIdStandardEnum.FundBenchmarkName }, name: "Benchmark Name" }
    ];
    if (currency && withInvestors) {
        datapoints.push({ datapoint: { standard: DatapointIdStandardEnum.FundInvestorId }, name: "InvestorId" }, { datapoint: { standard: DatapointIdStandardEnum.FundInvestorName }, name: "InvestorName" }, { datapoint: { standard: DatapointIdStandardEnum.FundInvestorCurrency }, name: "InvestorCurrency" }, { datapoint: { standard: DatapointIdStandardEnum.FundRemainingCommitment }, name: "Remaining Commitment" });
    }
    else if (!withInvestors) {
        datapoints.push({ datapoint: { standard: DatapointIdStandardEnum.FundReportedRemainingCommitment }, name: "Remaining Commitment" });
    }
    addCustomFieldsAsDatapoints(customFields, datapoints);
    // We always group by fund id
    const { dimensions, measures } = withInvestors
        ? getMeasuresAndDimensionsWithCurrency([...groups, "Id", "InvestorId", "ManagerId"], datapoints.slice(2))
        : getMeasuresAndDimensionsWithCurrency([], datapoints.slice(2));
    dimensions.push(datapoints[0]);
    return { dimensions, measures };
};
const useCreateColInsFunds = (isInvestorAccount, selectAllEnabled) => {
    const mainGridSettingsBenchmarkMode = useSelector((state) => state.mainGridSettings.setBenchmarkMode, isEqual);
    const assetClassCol = [];
    if (mainGridSettingsBenchmarkMode) {
        assetClassCol.push({
            field: "Benchmark Name",
            headerName: language.en.funds.columns.benchmarkName,
            type: CustomColumnType.AUX_TEXT_COLUMN
        });
    }
    assetClassCol.push({
        enableRowGroup: true,
        field: assetClass.gridTitle,
        headerName: language.en.funds.columns.assetClass,
        type: CustomColumnType.InsTextSetColumn
    });
    const investorCols = [
        { field: "InvestorId", hide: true, lockVisible: true },
        {
            field: "Name",
            headerName: language.en.funds.columns.investorFundName,
            initialSort: "asc",
            type: [CustomColumnType.InsDatapointLinkColumn]
        },
        { field: "InvestorName", headerName: language.en.funds.columns.investorAccountName, type: CustomColumnType.AUX_TEXT_COLUMN },
        ...assetClassCol,
        { enableRowGroup: true, field: "Currency", headerName: language.en.funds.columns.fundCurrency, type: CustomColumnType.InsTextSetColumn, width: 80 },
        { field: "InvestorCurrency", headerName: language.en.funds.columns.investorCurrency, type: CustomColumnType.InsTextSetColumn, width: 80 }
    ], noInvestorCols = [
        { field: "Name", headerName: language.en.funds.columns.fundName, initialSort: "asc", type: [CustomColumnType.InsDatapointLinkColumn] },
        ...assetClassCol,
        { enableRowGroup: true, field: "Currency", headerName: language.en.funds.columns.currency, type: CustomColumnType.InsTextSetColumn, width: 80 }
    ];
    const cols = [
        {
            field: "checkboxCol",
            floatingFilter: false,
            headerCheckboxSelection: selectAllEnabled,
            headerName: " ",
            type: AuxGridColumnType.AUX_CHECKBOX_COLUMN,
            width: 32
        },
        { field: "Id", hide: true, lockVisible: true },
        ...isInvestorAccount ? investorCols : noInvestorCols,
        { enableRowGroup: true, field: "ManagerName", headerName: language.en.funds.columns.managerName, type: CustomColumnType.AUX_TEXT_COLUMN },
        {
            field: "ManagerId",
            headerName: language.en.funds.columns.managerName,
            hide: true,
            lockVisible: true,
            type: CustomColumnType.AUX_TEXT_COLUMN,
            // Use refData instead ?
            valueFormatter: (p) => {
                let value = p.data?.ManagerName;
                if (p.node?.group && p.value && Array.isArray(p.node.allLeafChildren)) {
                    const [child] = p.node.allLeafChildren;
                    value = child?.data?.ManagerName;
                }
                return value;
            }
        },
        { enableRowGroup: true, field: "Strategy", headerName: language.en.funds.columns.strategy, type: CustomColumnType.InsTextSetColumn },
        { enableRowGroup: true, field: "Geography", headerName: language.en.funds.columns.geography, type: CustomColumnType.InsTextSetColumn },
        { aggFunc: InsGridAggs.totalData, field: "NAV", headerName: language.en.funds.columns.nav, type: [CustomColumnType.InsMoneyColumn] },
        { aggFunc: InsGridAggs.totalData, field: "Commitment", headerName: language.en.funds.columns.commitment, type: [CustomColumnType.InsMoneyColumn] },
        { aggFunc: InsGridAggs.totalData, field: "Contributions", headerName: language.en.funds.columns.contributions, type: CustomColumnType.InsMoneyColumn },
        { aggFunc: InsGridAggs.totalData, field: "Distributions", headerName: language.en.funds.columns.distributions, type: CustomColumnType.InsMoneyColumn },
        { aggFunc: InsGridAggs.totalData, field: "Remaining Commitment", headerName: language.en.funds.columns.remainingCommitment, type: CustomColumnType.InsMoneyColumn },
        {
            field: "tags",
            filter: "agSetColumnFilter",
            filterValueGetter: (params) => params.data.tags?.map((tag) => tag.label),
            headerName: "Tags",
            type: [CustomColumnType.InsTagsColumn, CustomColumnType.AUX_TEXT_COLUMN]
        }
    ];
    const customFields = useGetCustomDatapointFields(isInvestorAccount ? [DatapointsEntityType.Fund, "Investor Account"] : [DatapointsEntityType.Fund]);
    addCustomFieldsAsCols(customFields, cols);
    return cols;
};
export const InsFunds = () => {
    const dispatch = useDispatch(), fundsPage = useSelector((state) => state.mainGridSettings, isEqual);
    const withInvestors = fundsPage.setFundTab === FundsTabs.InvestorAccounts;
    const stateId = withInvestors ? "all-InvestorAccount-grid" : "all-funds-grid";
    const currency = useSelector((s) => gridCurrencySelector(s, stateId));
    useRefreshOnUserSettingsChange();
    const cols = useCreateColInsFunds(withInvestors, fundsPage.setPMIsMode);
    const groups = useGetGridGroups(stateId, cols);
    const { dimensions, measures } = useMeasuresAndDimensions(currency, groups, withInvestors);
    const request = useCustomDataDatapointsRequest(dimensions, measures, withInvestors ? DataExportGridTypes.InvestorAccounts : DataExportGridTypes.Funds, currency, "CustomFundsGrid"), [data, querystate, refresh] = useGetCustomDataDatapoints(request);
    const { data: dataTags } = useFetchTagsQuery({ entityType: withInvestors ? TagEntityType.INVESTOR : TagEntityType.FUND });
    const { rows, totals } = insDatapointGridWidgetAdapterData(data, cols);
    let newRows = [];
    if (withInvestors) {
        newRows = prepareTagRows(rows, dataTags, TagEntityType.INVESTOR);
    }
    else {
        newRows = prepareTagRows(rows, dataTags);
    }
    if (!dataTags?.tags) {
        newRows = rows;
    }
    const defaultLinksMapping = getDefaultMappings([{ dimensions, id: "", measures }], cols);
    const currencies = [...new Set(newRows?.map((r) => r.Currency || ""))].filter((c) => c);
    const datapointExcelOptions = getColumnExcelOptions(cols, measures);
    const totalGroupExcelOptions = getDatapointTotalGroupExcelOptions(groups, measures, dimensions);
    const resolveHeaderLeftSection = () => {
        if (fundsPage.setBenchmarkMode) {
            return React.createElement(InsFundsHeaderLeftBenchmark, { stateId: stateId });
        }
        if (fundsPage.setPMIsMode) {
            return React.createElement(InsBulkPMISelectionMenu, { stateId: stateId, entityType: EntityType.Fund });
        }
        return React.createElement(React.Fragment, null);
    };
    const fundExcelRequest = useCreateFundsDatapointsExcelRequest(stateId, dimensions, measures, datapointExcelOptions, totalGroupExcelOptions, withInvestors === false);
    return React.createElement(React.Fragment, null,
        React.createElement("div", { style: { marginBottom: "-1px" } },
            React.createElement(AuxTabBar, { onTabBarItemSelected: (event) => {
                    batch(() => {
                        dispatch(resetFundAndInvestorSelectedRows());
                        dispatch(setFundTab(event.detail.tab.uid));
                    });
                }, tabType: "classic", activeUid: fundsPage.setFundTab, data: [
                    { label: language.en.funds.gridTitle, uid: FundsTabs.Funds },
                    { label: language.en.funds.investorAccount, uid: FundsTabs.InvestorAccounts }
                ] })),
        React.createElement(InsGridWidget, { fundWidget: true, dataMenu: [
                [
                    useExportExcel(fundExcelRequest, fundsPage.setFundTab),
                    useSetBenchmarkMenuItem(stateId),
                    useSetPMIMenuItem(stateId)
                ]
            ], rowSelectedHandler: ((e) => {
                dispatch(setSelectedRowsState({
                    id: stateId,
                    selectedRows: e.api.getSelectedRows()
                        .map((row) => (row.InvestorId
                        ? `${row.Id}-${row.InvestorId}`
                        : row.Id))
                }));
            }), selectionMode: "multiple", widgetId: stateId, stateId: stateId, applyColumnsAutosize: true, columnDefs: cols, rowData: newRows, reduceHeight: true, refreshAction: refresh, totalData: totals, currencyData: currencies, queryState: querystate, context: { linksMapping: defaultLinksMapping }, suppressRowClickSelection: true, allowTags: true, tagsEntityType: withInvestors ? TagEntityType.INVESTOR : TagEntityType.FUND, headerLeft: resolveHeaderLeftSection() }));
};
